import { RADashboardCardId } from '@/constants/ReportDashboard/cards-enums'
import { CardTitleLookup } from '@/constants/ReportDashboard/cards-metadata'
import { isCardAllowedForDashboardType } from '@/constants/ReportDashboard/card-exclusivity'
import { FADashboardType } from '@/constants/ReportDashboard/dashboards/dashboard-types'
import { RADashboardLayout } from '@/interfaces/raDashboard.interface'
import { useMemo } from 'react'

const ALL_CARD_IDS = Object.values(RADashboardCardId)

type UseDashboardCardOptionsArgs = {
  layout: RADashboardLayout
  selectedTab: string
  setLayout: (newLayout: RADashboardLayout) => void
  dashboardType: FADashboardType
}

// Shared by DashboardGuiPalette (Editor/) and DashboardAddCardModal (MainPage/) so the
// set of pickable cards, their grouping/order, and the add behavior stay in sync.
export const useDashboardCardOptions = ({ layout, selectedTab, setLayout, dashboardType }: UseDashboardCardOptionsArgs) => {
  const usedCardIds = useMemo(
    () => new Set(
      Object.values(layout.tabLayouts)
        .flatMap(t => t.cardsOrder)
        .filter(id => !id.startsWith('empty_'))
    ),
    [layout.tabLayouts]
  )

  const { uniqueCards, datapointCards } = useMemo(() => {
    const sortedCardIds = ALL_CARD_IDS
      .filter(cardId => isCardAllowedForDashboardType(cardId, dashboardType))
      .sort((a, b) => CardTitleLookup[a].localeCompare(CardTitleLookup[b]))

    return {
      uniqueCards: sortedCardIds.filter(cardId => !cardId.includes('point_')),
      datapointCards: sortedCardIds.filter(cardId => cardId.includes('point_')),
    }
  }, [dashboardType])

  const isCardInUse = (cardId: RADashboardCardId) => usedCardIds.has(cardId)

  const addCard = (cardId: RADashboardCardId) => {
    const currentTabLayout = layout.tabLayouts[selectedTab]
    if (!currentTabLayout) return

    setLayout({
      ...layout,
      tabLayouts: {
        ...layout.tabLayouts,
        [selectedTab]: {
          ...currentTabLayout,
          cardsOrder: [...currentTabLayout.cardsOrder, cardId],
        },
      },
    })
  }

  return { uniqueCards, datapointCards, isCardInUse, addCard }
}
